import { useState, useEffect } from 'react';
import { ExternalLink, Share2, Sparkles, X, ChevronRight } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

const categoryColors = {
  skincare: { bg: 'from-rose-400 to-pink-500', badge: 'bg-rose-100 text-rose-700', icon: 'bg-rose-500' },
  makeup: { bg: 'from-fuchsia-400 to-purple-500', badge: 'bg-fuchsia-100 text-fuchsia-700', icon: 'bg-fuchsia-500' },
  haircare: { bg: 'from-amber-400 to-orange-500', badge: 'bg-amber-100 text-amber-700', icon: 'bg-amber-500' },
  pharmacy: { bg: 'from-teal-400 to-emerald-500', badge: 'bg-teal-100 text-teal-700', icon: 'bg-teal-500' },
};

const categoryLabels = {
  skincare: 'Face Care', makeup: 'Make Up', haircare: 'Hair Care', pharmacy: 'Pharma'
};

export default function WeeklyPicksCard({ onDismiss }) {
  const [picks, setPicks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => { fetchPicks(); }, []);

  const fetchPicks = async () => {
    try {
      const response = await authFetch(`${API}/weekly-picks`);
      if (response.ok) setPicks(await response.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const buildShareText = () => {
    if (!picks) return '';
    const lines = picks.picks.map((p, i) => 
      `${i + 1}. ${p.name} (${p.brand}) - ${p.price}\n   ${p.purchase_url}`
    ).join('\n');
    return `${picks.title} - ${picks.subtitle}\n\nTop picks this week:\n\n${lines}\n\nDiscover more on Gloverse: ${window.location.origin}`;
  };

  const handleShare = (platform) => {
    const text = buildShareText();
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(window.location.origin)}&text=${encodeURIComponent(text)}`
    };
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
      authFetch(`${API}/analytics/track-share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist_id: 'weekly_picks', medium: platform })
      });
    }
    setShowShareMenu(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(buildShareText());
    toast.success('Weekly picks copied!');
    authFetch(`${API}/analytics/track-share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checklist_id: 'weekly_picks', medium: 'copy_link' })
    });
    setShowShareMenu(false);
  };

  if (loading || !picks) return null;

  return (
    <div className="mx-4 my-3" data-testid="weekly-picks-card">
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 shadow-lg">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {picks.title}
              </h3>
              <p className="text-white/70 text-xs">{picks.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="relative">
              <button
                data-testid="weekly-picks-share"
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <Share2 className="w-4 h-4 text-white" />
              </button>
              {showShareMenu && (
                <div className="absolute right-0 top-10 bg-white rounded-xl shadow-xl border border-border p-2 z-50 min-w-[170px]">
                  <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium">WhatsApp</button>
                  <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium">Twitter</button>
                  <button onClick={() => handleShare('telegram')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium">Telegram</button>
                  <button onClick={copyLink} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium border-t border-border mt-1 pt-2">Copy</button>
                </div>
              )}
            </div>
            {onDismiss && (
              <button onClick={onDismiss} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-4 h-4 text-white/70" />
              </button>
            )}
          </div>
        </div>

        {/* Collapsed view — show category badges */}
        {!expanded && (
          <button
            data-testid="weekly-picks-expand"
            onClick={() => setExpanded(true)}
            className="w-full px-5 pb-5"
          >
            <div className="flex flex-wrap gap-2 mb-3">
              {picks.picks.map(p => {
                const colors = categoryColors[p.category] || categoryColors.skincare;
                return (
                  <span key={p.product_id} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${colors.badge}`}>
                    {categoryLabels[p.category]} Pick
                  </span>
                );
              })}
            </div>
            <div className="flex items-center gap-1 text-white/80 text-xs">
              <span>Tap to see this week's top picks</span>
              <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        )}

        {/* Expanded view — show products */}
        {expanded && (
          <div className="px-5 pb-5">
            <div className="space-y-2.5">
              {picks.picks.map((product, idx) => {
                const colors = categoryColors[product.category] || categoryColors.skincare;
                return (
                  <a
                    key={product.product_id}
                    href={product.purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`weekly-pick-${product.product_id}`}
                    className="flex items-center gap-3 p-3 bg-white/15 backdrop-blur-sm rounded-xl hover:bg-white/25 transition-all group"
                  >
                    <div className={`w-8 h-8 rounded-lg ${colors.icon} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                      #{idx + 1}
                    </div>
                    {product.image && (
                      <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${colors.badge}`}>
                          {categoryLabels[product.category]}
                        </span>
                        {product.usage_count > 0 && (
                          <span className="text-[9px] text-white/50">{product.usage_count} uses</span>
                        )}
                      </div>
                      <p className="text-white font-semibold text-sm truncate">{product.name}</p>
                      <p className="text-white/60 text-xs">{product.brand} • {product.price}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-white/70 shrink-0" />
                  </a>
                );
              })}
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="w-full mt-3 text-center text-white/60 text-xs hover:text-white/90 transition-colors"
            >
              Collapse
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
